import { useState } from 'react'
import { ref, uploadBytes } from 'firebase/storage'
import { storage } from '../../../../firebase'
import { X, UploadSimple, CheckCircle, WarningCircle, Spinner, Trash } from '@phosphor-icons/react'
import { Heading, Subheading } from '../../../../Components/Screen/Text/Heading'
import { Subtext, Text } from '../../../../Components/Screen/Text/Body'
import { Button, ButtonOutline } from '../../../../Components/Screen/Button/button'

const LAYERS = [
  { key: 'desmatamento', label: 'Desmatamento', color: 'text-red-400' },
  { key: 'reserva-legal', label: 'Reserva Legal', color: 'text-yellow-300' },
  { key: 'embargo', label: 'Embargo', color: 'text-orange-400' },
  { key: 'areas', label: 'Áreas da Propriedade', color: 'text-verdeClaro', multiple: true },
  { key: 'boundaries', label: 'Talhões (Boundaries)', color: 'text-blue-400' },
]

function FileSlot({ layer, file, onChange }) {
  return (
    <div className="flex flex-col gap-1">
      <Text className={layer.color}>{layer.label}</Text>
      <label className="flex items-center gap-3 border border-cinzaescuro rounded-xl px-4 py-3 cursor-pointer hover:border-verdeClaro transition">
        <UploadSimple size={20} className="text-verdeClaro shrink-0" />
        <Subtext className="truncate text-cinza">
          {file ? file.name : 'Selecionar arquivo .geojson'}
        </Subtext>
        <input
          type="file"
          accept=".geojson,application/geo+json,application/json"
          className="hidden"
          onChange={(e) => onChange(e.target.files[0] || null)}
        />
      </label>
    </div>
  )
}

function MultiFileSlot({ layer, files, onChange }) {
  const handleAdd = (e) => {
    const picked = Array.from(e.target.files || [])
    if (picked.length === 0) return
    onChange([...files, ...picked])
    e.target.value = ''
  }

  const handleRemove = (index) => {
    onChange(files.filter((_, i) => i !== index))
  }

  return (
    <div className="flex flex-col gap-1">
      <Text className={layer.color}>{layer.label}</Text>
      <label className="flex items-center gap-3 border border-cinzaescuro rounded-xl px-4 py-3 cursor-pointer hover:border-verdeClaro transition">
        <UploadSimple size={20} className="text-verdeClaro shrink-0" />
        <Subtext className="truncate text-cinza">
          {files.length === 0
            ? 'Selecionar arquivos .geojson (múltiplos)'
            : `Adicionar mais arquivos (${files.length} selecionado${files.length > 1 ? 's' : ''})`}
        </Subtext>
        <input
          type="file"
          accept=".geojson,application/geo+json,application/json"
          className="hidden"
          multiple
          onChange={handleAdd}
        />
      </label>
      {files.length > 0 && (
        <ul className="flex flex-col gap-1 mt-1">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${index}`}
              className="flex items-center justify-between gap-2 px-3 py-1.5 border border-cinzaescuro rounded-lg"
            >
              <Subtext className="truncate text-cinza">{file.name}</Subtext>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-cinza hover:text-red-400 transition shrink-0"
              >
                <Trash size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

async function mergeGeoJSONFiles(files) {
  const collections = await Promise.all(
    files.map((file) =>
      file.text().then((text) => {
        try {
          return JSON.parse(text)
        } catch {
          throw new Error(`Arquivo "${file.name}" não é um JSON válido`)
        }
      })
    )
  )

  const features = []
  collections.forEach((gj, i) => {
    if (gj?.type === 'FeatureCollection' && Array.isArray(gj.features)) {
      features.push(...gj.features)
    } else if (gj?.type === 'Feature') {
      features.push(gj)
    } else {
      throw new Error(`Arquivo "${files[i].name}" não é um GeoJSON válido`)
    }
  })

  return new Blob(
    [JSON.stringify({ type: 'FeatureCollection', features })],
    { type: 'application/geo+json' }
  )
}

export function ModalGeoJSONUpload({ nftId, onClose }) {
  const [files, setFiles] = useState({ desmatamento: null, 'reserva-legal': null, embargo: null, areas: [], boundaries: null })
  const [status, setStatus] = useState(null) // null | 'loading' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('')

  const hasAnyFile =
    Boolean(files.desmatamento) ||
    Boolean(files['reserva-legal']) ||
    Boolean(files.embargo) ||
    files.areas.length > 0 ||
    Boolean(files.boundaries)

  const setFile = (key) => (file) => setFiles((prev) => ({ ...prev, [key]: file }))

  const handleSubmit = async () => {
    if (!hasAnyFile) return
    setStatus('loading')
    setErrorMsg('')

    try {
      const uploads = []

      for (const key of ['desmatamento', 'reserva-legal', 'embargo', 'boundaries']) {
        const file = files[key]
        if (!file) continue
        const storageRef = ref(storage, `geojson/${nftId}/${key}.geojson`)
        uploads.push(uploadBytes(storageRef, file, { contentType: 'application/geo+json' }))
      }

      if (files.areas.length > 0) {
        const merged = await mergeGeoJSONFiles(files.areas)
        const storageRef = ref(storage, `geojson/${nftId}/areas.geojson`)
        uploads.push(uploadBytes(storageRef, merged, { contentType: 'application/geo+json' }))
      }

      await Promise.all(uploads)
      setStatus('success')
    } catch (err) {
      setErrorMsg(err.message || 'Erro ao fazer upload')
      setStatus('error')
    }
  }

  return (
    <div className="modal modal-open backdrop-blur">
      <div className="relative modal-box bg-preto border border-cinzaescuro max-w-lg w-full flex flex-col gap-5">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-cinza hover:text-branco transition"
        >
          <X size={22} />
        </button>

        <div className="flex flex-col gap-1">
          <Heading>Dados Geoespaciais</Heading>
          <Subtext className="text-cinza">Faça upload dos arquivos .geojson da propriedade</Subtext>
        </div>

        {status === 'success' ? (
          <div className="flex flex-col items-center gap-4 py-6">
            <CheckCircle size={56} className="text-verdeClaro" />
            <Subheading>Upload concluído!</Subheading>
            <Subtext className="text-cinza text-center">
              As camadas serão exibidas no mapa em alguns instantes.
            </Subtext>
            <Button onClick={onClose}>Fechar</Button>
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4">
              {LAYERS.map((layer) =>
                layer.multiple ? (
                  <MultiFileSlot
                    key={layer.key}
                    layer={layer}
                    files={files[layer.key]}
                    onChange={setFile(layer.key)}
                  />
                ) : (
                  <FileSlot
                    key={layer.key}
                    layer={layer}
                    file={files[layer.key]}
                    onChange={setFile(layer.key)}
                  />
                )
              )}
            </div>

            {status === 'error' && (
              <div className="flex items-center gap-2 text-red-400">
                <WarningCircle size={18} />
                <Subtext className="text-red-400">{errorMsg}</Subtext>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-2">
              <ButtonOutline onClick={onClose}>Cancelar</ButtonOutline>
              <Button onClick={handleSubmit} disabled={!hasAnyFile || status === 'loading'}>
                {status === 'loading' ? (
                  <span className="flex items-center gap-2">
                    <Spinner size={16} className="animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  'Enviar arquivos'
                )}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
